import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { productAvailable } from '../release-scope';
import { Tooltip } from './tooltip';
import { Grip } from 'lucide-react';
import type { DugziApp } from './apps-registry';
import { NotificationBell } from './bell';
import { ASK_EVENT, type AskRequest } from './ask';
import { AppRail, InRail, enableAppRail } from './apprail';
import { useT } from '../i18n';


export interface AppLauncherProps {
  /** The app this is (its registry id): marked as "you're here". */
  current: string;
  /** Dugzi Account's public URL (`me.accountsUrl`). */
  accountsUrl: string;
  /** The account this tab uses: other apps open in the same account. */
  account?: string;
  apps?: readonly DugziApp[];
  testId?: string;
  /** The notification bell next to the launcher (D-110): on wherever a signed-in account is known. */
  bell?: boolean;
}

/**
 * Getting between Dugzi apps: a grid of every live product (the current one marked), opening in the
 * same account, plus "New document / spreadsheet / presentation". A disclosure with links, not a
 * menu, so links behave like links (middle-click, open in new tab). Keyboard: arrows move (Up and
 * Down by row), Home and End jump, Escape closes and returns focus to the button. The panel's code
 * loads when the page is idle (or on the first click), so it isn't part of an app's first visit.
 */
export function AppLauncher({ current, accountsUrl, account, apps, testId = 'app-launcher', bell }: AppLauncherProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [ask, setAsk] = useState(false);
  // A question from a contextual action in the app ("Ask about this thread"; `askDugzi()`).
  const [asked, setAsked] = useState<(AskRequest & { at: number }) | null>(null);
  const [, loaded] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const Panel = panelModule;
  useEffect(() => { if (!panelModule) whenIdle(() => { void loadPanel().then(() => loaded((n) => n + 1)).catch(() => {}); }); }, []);
  useEffect(() => { if ((open || ask) && !panelModule) loadPanel().then(() => loaded((n) => n + 1)).catch(() => {}); }, [open, ask]);
  // ⌘J / Ctrl+J anywhere in a Dugzi app opens Ask Dugzi in a side panel (D-086), except where the page
  // has used the key already: in Docs' text and a Slides text box it justifies, as in Word (D-146).
  useEffect(() => {
    if (current === 'ai' || !productAvailable('ai')) return;
    const key = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'j') { e.preventDefault(); setOpen(false); setAsk((v) => !v); }
    };
    const onAsk = (e: Event) => { const d = (e as CustomEvent<AskRequest>).detail; if (d?.prompt) { setOpen(false); setAsked({ ...d, at: Date.now() }); setAsk(true); } };
    document.addEventListener('keydown', key);
    window.addEventListener(ASK_EVENT, onAsk);
    return () => { document.removeEventListener('keydown', key); window.removeEventListener(ASK_EVENT, onAsk); };
  }, [current]);
  const close = (focus: boolean) => { setOpen(false); if (focus) ref.current?.querySelector<HTMLButtonElement>('button')?.focus(); };
  useEffect(() => {
    if (!open || !Panel) return;
    const outside = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(true); } };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', esc, true);
    requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('[aria-current="page"], .dz-apps__tile')?.focus());
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', esc, true); };
  }, [open, !!Panel]); // eslint-disable-line react-hooks/exhaustive-deps
  const src = productAvailable('ai') && Panel && ask ? Panel.askSrc(current, accountsUrl, account, apps, asked ?? undefined) : null;

  const showBell = bell ?? (!!account && current !== 'account');
  // The compact app bar (D-161): the launcher mounts it; the grid button becomes its "More".
  useLayoutEffect(() => { enableAppRail(); }, []);
  const more = (
    <div className="dz-apps" ref={ref} role="presentation" onBlur={(e) => { if (open && !ref.current?.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <Tooltip content="More apps" placement="right" disabled={open}>
        <button type="button" className="dz-icon-btn dz-apps__btn dz-rail__item" aria-expanded={open} aria-controls={open && Panel ? id : undefined} aria-label={t('ui.apps.label')}
          onClick={() => setOpen((o) => !o)} data-testid={testId}>
          <span className="dz-rail__icon"><Grip aria-hidden size={22} /></span>
        </button>
      </Tooltip>
      {open && Panel && <Panel.LauncherPanel id={id} current={current} accountsUrl={accountsUrl} account={account} apps={apps} testId={testId} root={ref} close={close} onAsk={() => { setOpen(false); setAsk(true); }} />}
    </div>
  );
  return (
    <>
    {showBell && <InRail slot="bell"><NotificationBell account={account} accountsUrl={accountsUrl} /></InRail>}
    <AppRail current={current} accountsUrl={accountsUrl} account={account} apps={apps} onAsk={() => { setOpen(false); setAsk((v) => !v); }} more={more} />
    {src && Panel && <Panel.AskDugziPanel src={src} onClose={() => { setAsk(false); setAsked(null); }} />}
    </>
  );
}

let panelModule: typeof import('./apps-panel') | null = null;
const loadPanel = () => import('./apps-panel').then((m) => { panelModule = m; return m; });
function whenIdle(fn: () => void) {
  const w = window as Window & { requestIdleCallback?(cb: () => void, o?: { timeout: number }): number };
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 2000 }); else setTimeout(fn, 300);
}

/**
 * Arrived from a "New …" shortcut (`?new=1`)? Returns true once and tidies the address bar, so a
 * reload or Back doesn't make another one. Apps call it on their home page and create one.
 */
export function takeNewRequest(): boolean {
  const u = new URL(location.href);
  if (u.searchParams.get('new') !== '1') return false;
  u.searchParams.delete('new');
  history.replaceState(history.state, '', u.pathname + u.search + u.hash);
  return true;
}
